<template>
  <page-section :title="$t('ConsentAuditLog_Title')">
    <rest-list :payload="fetchEndpoint" :per-page="50" ref="restList">
      <rest-list-column-date
        prop="create_time"
        :title="$t('ConsentAuditLog_Field_Date')"
        :width="200"
        :sortable="true"
        :filterable="true"
      />

      <rest-list-column-enum
        prop="action"
        :title="$t('ConsentAuditLog_Field_Action')"
        :enum="ConsentPreferenceAction"
        :sortable="false"
        :filterable="true"
        :width="150"
      />

      <rest-list-column-enum
        prop="type"
        :title="$t('ConsentAuditLog_Field_Type')"
        :enum="ConsentPreferenceType"
        :sortable="false"
        :filterable="true"
        :width="150"
      />

      <rest-list-column
        prop="name"
        :title="$t('ConsentAuditLog_Field_Name')"
        :sortable="false"
        :filterable="true"
        :width="170"
      />

      <rest-list-column
        prop="updated_by"
        :title="$t('ConsentAuditLog_Field_UpdatedBy')"
        :filterable="false"
        :sortable="false"
        :width="150"
        :filter-input="false"
      />

      <rest-list-column
        prop="changes"
        :title="$t('ConsentAuditLog_Field_Summary')"
        :sortable="false"
        :filterable="false"
      >
        <template v-slot="scope">
          <div class="summary" v-if="scope.row.changes">
            {{ formatSummary(scope.row) }}
          </div>
          <template v-else>{{
            $t('ConsentAuditLog_Summary_NoChange')
          }}</template>
        </template>
      </rest-list-column>
    </rest-list>
  </page-section>
</template>

<script>
import PageSection from '@/common/components/page/PageSection'
import RestList from '@/common/components/rest/RestList'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import { projectEndpoint } from '@/core/helpers'
import {
  ConsentPreferenceAction,
  ConsentPreferenceActionValue,
  ConsentPreferenceType
} from '@/modules/consent-manager/enum/ConsentAuditLogEnums'
import { consentPreferenceType as PreferenceTypeLabels } from '@/modules/consent-manager/enum/ConsentPreferenceEnums'
import { SubscriptionPreferenceChannelLabels } from '@/modules/settings/enum/SubscriptionPreferenceChannelEnum'

export default {
  name: 'ConsentAuditLog',

  components: {
    RestListColumnDate,
    RestListColumn,
    RestListColumnEnum,
    RestList,
    PageSection
  },

  data() {
    return {
      ConsentPreferenceAction,
      ConsentPreferenceType
    }
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'consent-audit-log'
      })
    }
  },

  watch: {
    '$route.query': {
      handler(query) {
        this.$nextTick(() => {
          if (this.$refs.restList) {
            if (query.name) {
              this.$refs.restList.filterField('name', query.name)
            }

            if (query.type !== undefined) {
              this.$refs.restList.filterField('type', query.type)
            }
          }
        })
      },
      immediate: true
    }
  },

  methods: {
    formatSummary(row) {
      const { changes, action } = row

      if (
        !changes ||
        typeof changes !== 'object' ||
        Object.keys(changes).length === 0
      ) {
        return this.$t('ConsentAuditLog_Summary_NoChange')
      }

      const parts =
        action === ConsentPreferenceActionValue.CREATE
          ? this.createParts(changes)
          : this.updateParts(changes)

      return parts.length > 0
        ? parts.join('\n')
        : this.$t('ConsentAuditLog_Summary_NoChange')
    },

    createParts(changes) {
      const name = this.newValue(changes.name)
      const displayName = this.newValue(changes.display_name)
      const type = this.newValue(changes.type)
      const channels = this.enabledChannels(this.newValue(changes.channels))
      const parts = []

      if (!this.isEmpty(name)) {
        parts.push(this.$t('ConsentAuditLog_Summary_Name', { name }))
      }

      if (!this.isEmpty(displayName)) {
        parts.push(
          this.$t('ConsentAuditLog_Summary_DisplayName', { displayName })
        )
      }

      if (!this.isEmpty(type)) {
        parts.push(
          this.$t('ConsentAuditLog_Summary_Type', {
            type: this.typeLabel(type)
          })
        )
      }

      if (channels.length > 0) {
        parts.push(
          this.$t('ConsentAuditLog_Summary_Channels', {
            channels: channels.join(', ')
          })
        )
      }

      return parts
    },

    updateParts(changes) {
      const summaryParts = []

      if (changes.display_name) {
        const displayNameSummary = this.displayNameChange(changes.display_name)

        if (displayNameSummary) {
          summaryParts.push(displayNameSummary)
        }
      }

      if (changes.channels) {
        const original = changes.channels.original || {}
        const updated = changes.channels.new || {}

        const addedChannels = []
        const removedChannels = []

        Object.keys(updated).forEach(channel => {
          if (
            updated[channel] === 1 &&
            (!original[channel] || original[channel] === 0)
          ) {
            addedChannels.push(this.formatChannelName(channel))
          }
        })

        Object.keys(original).forEach(channel => {
          if (
            original[channel] === 1 &&
            (!updated[channel] || updated[channel] === 0)
          ) {
            removedChannels.push(this.formatChannelName(channel))
          }
        })

        if (addedChannels.length > 0) {
          summaryParts.push(
            this.$t('ConsentAuditLog_Summary_ChannelAdded', {
              channels: addedChannels.join(', ')
            })
          )
        }

        if (removedChannels.length > 0) {
          summaryParts.push(
            this.$t('ConsentAuditLog_Summary_ChannelRemoved', {
              channels: removedChannels.join(', ')
            })
          )
        }
      }

      return summaryParts
    },

    displayNameChange(displayName) {
      const newDisplayName = displayName.new
      const oldDisplayName = displayName.original

      if (this.isEmpty(newDisplayName) && this.isEmpty(oldDisplayName)) {
        return null
      }

      if (this.isEmpty(newDisplayName)) {
        return this.$t('ConsentAuditLog_Summary_DisplayNameCleared', {
          oldDisplayName
        })
      }

      if (this.isEmpty(oldDisplayName)) {
        return this.$t('ConsentAuditLog_Summary_DisplayNameChanged', {
          newDisplayName
        })
      }

      return this.$t('ConsentAuditLog_Summary_DisplayNameChangedFrom', {
        oldDisplayName,
        newDisplayName
      })
    },

    enabledChannels(channels) {
      if (!channels || typeof channels !== 'object') {
        return []
      }

      return Object.keys(channels)
        .filter(channel => channel && channels[channel] === 1)
        .map(channel => this.formatChannelName(channel))
    },

    isEmpty(value) {
      return value === null || value === undefined || value === ''
    },

    newValue(field) {
      if (field && typeof field === 'object' && 'new' in field) {
        return field.new
      }

      return undefined
    },

    typeLabel(type) {
      return PreferenceTypeLabels[type]
        ? this.$t(PreferenceTypeLabels[type])
        : type
    },

    formatChannelName(channel) {
      const channelKey = channel.toLowerCase()

      if (SubscriptionPreferenceChannelLabels[channelKey]) {
        return this.$t(SubscriptionPreferenceChannelLabels[channelKey])
      }

      return channel
    }
  }
}
</script>

<style scoped>
.summary {
  white-space: pre-line;
}
</style>



// WEBPACK FOOTER //
// src/modules/consent-manager/pages/ConsentAuditLog.vue